import React, { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { Link } from 'react-router-dom';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation';
import RingilDataTable, {
  ColumnConfig,
  DateTimeFormatType,
  FilterConfig,
  FilterType,
  MassActionConfig,
  OptionType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import {
  BatchResendRejectedDTO,
  BatchResendResolvedEmailsInDTO,
  BatchResendResolvedEmailsOutDTO,
  EmailHistoryAggStatusEnum,
  EmailHistoryRecipientRowOutDTO,
  FindEmailHistoryFilter,
  NotificationChannelsEnum,
  Pageable,
  PageResponseEmailHistoryRecipientRowOutDTO,
  ReasonEnum,
} from '@api/ringil-api.v2';
import { Callbacks } from '@api/apiRuntime';
import { RuleInput } from '@components/security/securedDecoration';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import COTag from '@components/FigmaComponents/COTag.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import MoreButton from '@components/RingilComponents/MoreButton/MoreButton.tsx';
import SvgFilter from '@icons/filter.svg?react';
import SvgRepeat from '@icons/Repeat';
import SvgCoCopy from '@icons/CoCopy.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import EmailStageStrip from './EmailStageStrip.tsx';
import { expandUuid, shortenUuid } from './shortUuid';
import { isNotBlank } from '@utils/utils';
import { aggStatusLabel, channelLabel, EMAIL_HISTORY_ENTITY_TYPES, entityTypeLabel, entityUrlInfo } from './emailHistoryLabels';

/** One row is one e-mail to one person; every row carries its request's header inline. */
export type EmailHistoryRow = EmailHistoryRecipientRowOutDTO;

export interface EmailHistoryTableHandle {
  reload: () => void;
}

/** Bean validation on the batch endpoint; larger selections are split across calls. */
const RESEND_BATCH_LIMIT = 50;

/** The API stops counting here and returns the cap, so above it `total` is a floor, not a count. */
const TOTAL_CAP = 10000;

const REJECTION_LABEL: Record<ReasonEnum, string> = {
  [ReasonEnum.NOT_FOUND]: 'not found',
  [ReasonEnum.NOT_EMAIL_CHANNEL]: 'not an e-mail',
  [ReasonEnum.ALREADY_RESENT]: 'already resent',
};

const summarizeRejections = (rejected: BatchResendRejectedDTO[]): string =>
  Object.entries(
    rejected.reduce<Record<string, number>>((acc, item) => {
      const label = REJECTION_LABEL[item.reason] ?? item.reason;
      acc[label] = (acc[label] ?? 0) + 1;
      return acc;
    }, {}),
  )
    .map(([label, count]) => `${count} ${label}`)
    .join(', ');

export const normalizeEmailHistoryFilter = (value?: any): FindEmailHistoryFilter => {
  const result = { ...(value ?? {}) };
  // The control holds the shortened form the column shows; the backend matches on the uuid.
  if (isNotBlank(result.requestId)) result.requestId = expandUuid(result.requestId) ?? result.requestId;
  return result as FindEmailHistoryFilter;
};

interface ComponentProps {
  fetchPage: (pageable: Pageable, filter: FindEmailHistoryFilter, onSuccess: (resp: PageResponseEmailHistoryRecipientRowOutDTO) => void) => void;
  /** Admin and tenant reach the same batch endpoint under different operations. */
  resendBatch: (body: BatchResendResolvedEmailsInDTO, onSuccess: (resp: BatchResendResolvedEmailsOutDTO) => void, hashAuthentication?: string, callbacks?: Callbacks) => void;
  /** Gates both resend affordances; passed as a flag so security mode can show what hid them. */
  resendSecured?: RuleInput;
  filterName: string;
  extraColumns?: ColumnConfig<EmailHistoryRow>[];
  extraFilters?: FilterConfig[];
  /** The applied filter, for owners whose controls need to reflect a value restored from URL/storage. */
  onFilterChange?: (filter: FindEmailHistoryFilter) => void;
}

const EmailHistoryTable = forwardRef<EmailHistoryTableHandle, ComponentProps>(
  ({ fetchPage, resendBatch, resendSecured, filterName, extraColumns, extraFilters, onFilterChange }, ref) => {
    const navigation = useNavigation();
    const { tr } = useTranslationLgs();
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const { showToastMessage } = useContext(ToastContext);
    const tableRef = useRef<{
      reloadData: () => void;
      selectedObjects: EmailHistoryRow[];
      clear: () => void;
      applyFilterValue: (property: string, value: any) => void;
    }>(null);
    /** Rows whose events are shown, keyed by resolved e-mail id. Purely presentational. */
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [pendingResend, setPendingResend] = useState<EmailHistoryRow[] | null>(null);
    const [resending, setResending] = useState(false);

    useImperativeHandle(ref, () => ({ reload: () => tableRef.current?.reloadData() }));

    const loadDataFromApi = (filter: FindEmailHistoryFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<EmailHistoryRow>) => void) => {
      onFilterChange?.(filter);
      fetchPage(pageable, filter, resp =>
        onSuccess({
          data: resp?.data ?? [],
          total: resp?.total ?? 0,
          page: resp?.page ?? pageable.pageNumber,
          size: resp?.size ?? pageable.pageSize,
        }),
      );
    };

    const toggleExpanded = (rowId: string) => {
      setExpandedIds(current => {
        const next = new Set(current);
        if (next.has(rowId)) next.delete(rowId);
        else next.add(rowId);
        return next;
      });
    };

    const copyCommunicationId = (requestId: string) => {
      navigator.clipboard.writeText(shortenUuid(requestId)).then(() => showToastMessage(tr('EmailHistory.toastIdCopied', 'Communication ID copied'), requestId, 'success'));
    };

    /**
     * Everything a mail administrator needs to trace one delivery, as plain text to paste into a
     * ticket. Timestamps stay in their raw ISO form rather than the table's localised format — this
     * is correlated against mail-server logs, where an unambiguous instant matters more than
     * readability.
     */
    const deliveryDetails = (row: EmailHistoryRow): string => {
      const lines = [`Recipient: ${row.recipientEmail ?? '—'}`];
      if (isNotBlank(row.rfcMessageId)) lines.push(`RFC message ID: ${row.rfcMessageId}`);
      const events = row.events ?? [];
      lines.push('Events:');
      lines.push(
        ...(events.length
          ? events.map(event => {
              const repeat = (event.count ?? 1) > 1 ? ` (${event.count}×, last ${event.lastAt})` : '';
              return `  ${event.createdAt}  ${event.type}${repeat}${event.description ? ` — ${event.description}` : ''}`;
            })
          : ['  (none)']),
      );
      return lines.join('\n');
    };

    const copyDeliveryDetails = (row: EmailHistoryRow) => {
      navigator.clipboard
        .writeText(deliveryDetails(row))
        .then(() =>
          showToastMessage(
            tr('EmailHistory.toastDetailsCopied', 'Details copied'),
            tr('EmailHistory.toastDetailsCopiedDetail', 'Paste them into the ticket for the mail administrator'),
            'success',
          ),
        );
    };

    /** A row already resent once is not resendable, so a selection may resend a subset of itself. */
    const resendableOf = (rows: EmailHistoryRow[]) => rows.filter(row => row.canResend);

    const runResend = (rows: EmailHistoryRow[]) => {
      const targets = resendableOf(rows);
      if (targets.length === 0) {
        setPendingResend(null);
        return;
      }
      const batches: string[][] = [];
      for (let i = 0; i < targets.length; i += RESEND_BATCH_LIMIT) batches.push(targets.slice(i, i + RESEND_BATCH_LIMIT).map(target => target.id));

      setResending(true);
      let remaining = batches.length;
      let accepted = 0;
      const rejected: BatchResendRejectedDTO[] = [];
      const settle = () => {
        remaining -= 1;
        if (remaining > 0) return;
        setResending(false);
        setPendingResend(null);
        // Sending runs in the background with nothing to poll, so a reload here would show nothing
        // new — only the selection is reset.
        const detail = rejected.length ? `${accepted} accepted, ${rejected.length} rejected (${summarizeRejections(rejected)})` : `${accepted} accepted`;
        showToastMessage(tr('EmailHistory.toastResendRequested', 'Resend requested'), detail, rejected.length ? 'warn' : 'success');
        tableRef.current?.clear();
      };
      batches.forEach(resolvedEmailIds =>
        resendBatch({ resolvedEmailIds }, response => {
          accepted += response?.acceptedIds?.length ?? 0;
          rejected.push(...(response?.rejected ?? []));
          settle();
        }, null, {
          // The generic mutation toast would say "change saved" — nothing was saved and the send is
          // only queued, so it is suppressed in favour of the accepted/rejected summary below.
          successToast: false,
          // onError suppresses the default toast, so raise it explicitly and still settle.
          onError: (_error: unknown, showStandardError: () => void) => {
            showStandardError();
            settle();
          },
        }),
      );
    };

    const renderCommunication = (row: EmailHistoryRow) => {
      const expanded = expandedIds.has(row.id);
      const events = row.events ?? [];
      return (
        <div className="flex flex-col items-start gap-2">
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => copyCommunicationId(row.requestId)}
              title={row.requestId}
              className="cursor-pointer self-start border-none bg-transparent p-0 text-left font-['DM_Sans'] text-xs text-content-secondary hover:text-content-brand"
              data-testid="email-history-communication-id">
              ID: {shortenUuid(row.requestId)}
            </button>
            <span className="font-medium text-content-brand">{row.subject ?? row.actionTitle ?? row.action}</span>
          </div>
          {(row.relatedEntities ?? []).map(entity => {
            const urlInfo = entityUrlInfo(navigation.urlFunctions, entity);
            return (
              <COTag key={`${entity.entityType}-${entity.entityId}`} label={entityTypeLabel(tr, entity.entityType)}>
                {urlInfo ? (
                  <Link to={navigation.createNavigationLink(urlInfo)} className="text-content-brand no-underline hover:underline">
                    {entity.applicationId}
                  </Link>
                ) : (
                  entity.applicationId
                )}
              </COTag>
            );
          })}
          {expanded &&
            events.map((event, index) => {
              // Repeats are collapsed into one entry spanning createdAt (first) → lastAt (last), and the
              // description belongs to the last one — so the last occurrence leads.
              const repeats = (event.count ?? 1) > 1;
              return (
                <div key={index} className="flex flex-col">
                  <span className="text-xs font-bold text-content-primary">
                    {stringDateTimeWithLocaleFromApiStringInTz(event.lastAt ?? event.createdAt, loggedUser?.defaultTimezoneId)}
                  </span>
                  <span className="text-xs text-content-primary">{event.description ?? event.type}</span>
                  {repeats && (
                    <span className="text-xs text-content-secondary">
                      {`${event.count}× · first ${stringDateTimeWithLocaleFromApiStringInTz(event.createdAt, loggedUser?.defaultTimezoneId)}`}
                    </span>
                  )}
                </div>
              );
            })}
          {events.length > 0 && (
            <button
              type="button"
              onClick={() => toggleExpanded(row.id)}
              className="inline-flex items-center gap-2 border-none bg-transparent p-0 font-['DM_Sans'] text-sm text-content-brand cursor-pointer transition-all duration-200 hover:text-complementary-brandDark">
              {expanded ? tr('EmailHistory.lessInfo', 'Less info') : tr('EmailHistory.moreInfo', 'More info')}
              <SvgChevron className={expanded ? 'rotate-180' : undefined} />
            </button>
          )}
        </div>
      );
    };

    const renderParty = (name?: string, phone?: string, email?: string) => (
      <div className="flex flex-col">
        <span className="text-content-primary">{name}</span>
        {phone && <span className="text-sm text-content-secondary">{phone}</span>}
        {email && <span className="text-sm text-content-brand break-all">{email}</span>}
      </div>
    );

    const renderChannel = (row: EmailHistoryRow) => (
      <div className="flex flex-col gap-1">
        <span className="whitespace-nowrap text-sm text-content-primary">
          {stringDateTimeWithLocaleFromApiStringInTz(row.emailCreatedAt ?? row.requestCreatedAt, loggedUser?.defaultTimezoneId)}
        </span>
        {/* The endpoint is email-scoped, so EMAIL stands in until the DTO carries a channel. */}
        <span className="text-sm text-content-primary">{channelLabel(tr, NotificationChannelsEnum.EMAIL)}</span>
        <EmailStageStrip stages={row} />
      </div>
    );

    const renderSender = (row: EmailHistoryRow) => renderParty(row.senderName, row.senderPhone, row.senderEmail);

    const renderRecipient = (row: EmailHistoryRow) => renderParty(row.recipientName, row.recipientPhone, row.recipientEmail);

    // System-generated mail has no acting user, so both lines would be blank; one dash reads better
    // than an empty cell.
    const renderPerformedBy = (row: EmailHistoryRow) =>
      isNotBlank(row.createdByName) || isNotBlank(row.createdByCompanyName) ? (
        <div className="flex flex-col">
          <span className="text-content-primary">{row.createdByName}</span>
          <span className="text-content-primary">{row.createdByCompanyName}</span>
        </div>
      ) : (
        <span className="text-content-secondary">—</span>
      );

    const renderActions = (row: EmailHistoryRow) => (
      <MoreButton
        buttonMode="table"
        dataTestId={`email-history-row-actions-${row.id}`}
        sections={[
          {
            items: [
              {
                id: 'filterByCommunication',
                label: tr('EmailHistory.filterByCommunicationId', 'Filter by communication ID'),
                icon: <SvgFilter />,
                command: () => tableRef.current?.applyFilterValue('requestId', shortenUuid(row.requestId)),
                dataTestId: 'email-history-filter-by-id',
              },
              {
                id: 'copyDetails',
                label: tr('EmailHistory.copyDetails', 'Copy details'),
                icon: <SvgCoCopy />,
                // Nothing to trace without either identifier or a single event.
                disabled: !isNotBlank(row.rfcMessageId) && (row.events?.length ?? 0) === 0,
                command: () => copyDeliveryDetails(row),
                dataTestId: 'email-history-copy-details',
              },
              {
                id: 'resend',
                label: tr('EmailHistory.resend', 'Resend'),
                icon: <SvgRepeat />,
                disabled: !row.canResend,
                command: () => setPendingResend([row]),
                dataTestId: 'email-history-resend',
                secured: resendSecured,
                securedId: 'more-email-history-resend',
              },
            ],
          },
        ]}
      />
    );

    const coreColumns: ColumnConfig<EmailHistoryRow>[] = [
      { id: 'communication', headerRender: () => tr('EmailHistory.columnCommunication', 'Communication'), width: '28%', order: 10, render: renderCommunication },
      { id: 'channel', headerRender: () => tr('EmailHistory.columnChannel', 'Channel / Status'), width: '16%', order: 20, render: renderChannel },
      {
        id: 'requestCreatedAt',
        field: 'requestCreatedAt',
        header: 'EmailHistory.createdAt',
        headerDefault: 'Created at',
        hidden: true,
        sortable: true,
        isDefaultSort: true,
        order: 25,
        render: () => null,
      },
      { id: 'sender', headerRender: () => tr('EmailHistory.columnSender', 'From'), width: '16%', order: 30, render: renderSender },
      { id: 'recipient', headerRender: () => tr('EmailHistory.columnRecipient', 'To'), width: '16%', order: 40, render: renderRecipient },
      { id: 'performedBy', headerRender: () => tr('EmailHistory.columnPerformedBy', 'Performed by / ID'), width: '16%', order: 50, render: renderPerformedBy },
      { id: 'actions', headerRender: () => '', width: '4%', order: 60, render: renderActions },
    ];

    const statusOptions: OptionType[] = Object.values(EmailHistoryAggStatusEnum).map(status => ({
      label: aggStatusLabel(tr, status),
      value: status,
      active: true,
    }));

    const entityTypeOptions: OptionType[] = EMAIL_HISTORY_ENTITY_TYPES.map(entityType => ({
      label: entityTypeLabel(tr, entityType),
      value: entityType,
      active: true,
    }));

    const coreFilters: FilterConfig[] = [
      { filterType: FilterType.InputText, label: tr('EmailHistory.columnCommunication', 'Communication'), placeholder: tr('EmailHistory.columnCommunication', 'Communication'), value: ['subject'], dataTestId: 'email-history-filter-subject' },
      { filterType: FilterType.InputText, label: tr('EmailHistory.filterApplicationId', 'Communication ID'), placeholder: tr('EmailHistory.filterApplicationId', 'Communication ID'), value: ['requestId'], dataTestId: 'email-history-filter-requestId' },
      { filterType: FilterType.Dropdown, label: tr('EmailHistory.status', 'Status'), placeholder: tr('EmailHistory.status', 'Status'), value: ['emailHistoryAggStatus'], options: statusOptions, dataTestId: 'email-history-filter-status' },
      { filterType: FilterType.Dropdown, label: tr('EmailHistory.entity', 'Entity'), placeholder: tr('EmailHistory.entity', 'Entity'), value: ['entityType'], options: entityTypeOptions, dataTestId: 'email-history-filter-entityType' },
      {
        filterType: FilterType.Calendar,
        label: tr('EmailHistory.filterCreated', 'Time'),
        placeholder: tr('EmailHistory.filterCreated', 'Time'),
        value: ['createdFrom', 'createdTill'],
        dateTimeFormat: DateTimeFormatType.ISOString,
        dataTestId: 'email-history-filter-created',
      },
      { filterType: FilterType.InputText, label: tr('EmailHistory.columnSender', 'From'), placeholder: tr('EmailHistory.columnSender', 'From'), value: ['senderName'], dataTestId: 'email-history-filter-sender' },
      { filterType: FilterType.InputText, label: tr('EmailHistory.columnRecipient', 'To'), placeholder: tr('EmailHistory.columnRecipient', 'To'), value: ['recipientEmail'], dataTestId: 'email-history-filter-recipient' },
      {
        filterType: FilterType.InputText,
        label: tr('EmailHistory.filterPerformedBy', 'Performed by'),
        placeholder: tr('EmailHistory.filterPerformedBy', 'Performed by'),
        value: ['createdByName'],
        // Eighth in the bar, so past the 7 the chip row seeds by position — but the design shows it.
        defaultShown: true,
        dataTestId: 'email-history-filter-createdBy',
      },
    ];

    const massActionConfig: MassActionConfig<EmailHistoryRow>[] = [
      {
        label: tr('EmailHistory.resendSelected', 'Resend'),
        icon: <SvgRepeat />,
        disabled: selected => resendableOf(selected).length === 0,
        onClick: () => setPendingResend(tableRef.current?.selectedObjects ?? []),
        secured: () => ({ id: 'email-history-resend-selected', label: tr('EmailHistory.resendSelected', 'Resend'), description: 'Resend the selected e-mails', visible: resendSecured }),
      },
    ];

    return (
      <>
        <div className="overflow-x-auto">
          <RingilDataTable
            ref={tableRef}
            columnConfig={[...coreColumns, ...(extraColumns ?? [])]}
            filtersConf={[...coreFilters, ...(extraFilters ?? [])]}
            loadDataFromApi={loadDataFromApi}
            filterName={filterName}
            normalizeFilter={normalizeEmailHistoryFilter}
            massActionConfig={massActionConfig}
            totalCapAt={TOTAL_CAP}
            // Resend is the only mass action, and it silently skips anything already resent.
            isRowSelectable={row => row.canResend}
          />
        </div>
        <ConfirmDialog
          visible={!!pendingResend}
          loading={resending}
          dialogTitle={tr('EmailHistory.resendDialogTitle', 'Resend')}
          dialogMessage={tr('EmailHistory.resendDialogMessage', 'Do you really want to resend the selected messages?')}
          onConfirm={() => runResend(pendingResend ?? [])}
          onCancel={() => setPendingResend(null)}
          onAcceptButtonText={tr('EmailHistory.resend', 'Resend')}
          onCancelButtonText={tr('generic.cancel', 'Cancel')}
        />
      </>
    );
  },
);

EmailHistoryTable.displayName = 'EmailHistoryTable';

export default EmailHistoryTable;
